import {ColorEnum} from "@/modules/vue/enums";
import {ComputedRef} from "vue";
import {app} from "@/di";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {DialogService} from "@/modules/vue/services/DialogService";

export function usePagination(perPageInitial = 10, startPage = 1) {
  const route = useRoute();
  const router = useRouter();

  const {
    perPage,
    currentPage,
    pageCount,
    showPrevPage,
    showNextPage,
    // @ts-ignore
  } = usePaginationWithoutRoute(perPageInitial, Number.parseInt(route.query.page as unknown as number) || startPage);

  watch(() => currentPage.value, () => {
    router.replace({
      name: route.name!,
      params: route.params,
      query: Object.assign({}, route.query, {page: currentPage.value})
    });
  });

  return {perPage, currentPage, pageCount, showPrevPage, showNextPage};
}

export function usePaginationWithoutRoute(perPageInitial = 10, startPage = 1) {
  const perPage = ref(perPageInitial);
  const currentPage = ref(startPage);
  const pageCount = ref(0);

  const showPrevPage = () => currentPage.value = Math.max(1, currentPage.value - 1);
  const showNextPage = () => currentPage.value = Math.min(pageCount.value, currentPage.value + 1);

  return {perPage, currentPage, pageCount, showPrevPage, showNextPage};
}

export function useDebug() {
  return computed(() => app.get(ConfigRepository).get('debug'));
}

export function computedColorClasses(value: ColorEnum, options: { bg: number, text?: number } | { bg?: number, text: number }): ComputedRef<string> {
  const map = generateColorMap(options);

  return computed(() => map[value]);
}

export function getColorValue(color: ColorEnum, value: 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900): string {
  return ColorValue[color][value];
}

export function generateColorMap(
  options: { focus?: number, border?: number, active?: number, hover?: number, darkBg?: number, darkText?: number, bg?: number, text?: number },
): { [key in ColorEnum]: string } {
  const map: { [key in ColorEnum]: string } = {
    [ColorEnum.Slate]: '',
    [ColorEnum.Gray]: '',
    [ColorEnum.Zinc]: '',
    [ColorEnum.Neutral]: '',
    [ColorEnum.Stone]: '',
    [ColorEnum.Red]: '',
    [ColorEnum.Orange]: '',
    [ColorEnum.Amber]: '',
    [ColorEnum.Yellow]: '',
    [ColorEnum.Lime]: '',
    [ColorEnum.Green]: '',
    [ColorEnum.Emerald]: '',
    [ColorEnum.Teal]: '',
    [ColorEnum.Cyan]: '',
    [ColorEnum.Sky]: '',
    [ColorEnum.Blue]: '',
    [ColorEnum.Indigo]: '',
    [ColorEnum.Violet]: '',
    [ColorEnum.Purple]: '',
    [ColorEnum.Fuchsia]: '',
    [ColorEnum.Pink]: '',
    [ColorEnum.Rose]: '',
    [ColorEnum.Bronze]: '',
    [ColorEnum.Silver]: '',
    [ColorEnum.Gold]: '',
  };

  Object.values(ColorEnum).forEach((color: ColorEnum) => {
    const classes = [];

    if (options.bg) {
      classes.push(`bg-${color}-${options.bg}`);
    }

    if (options.darkText) {
      classes.push(`dark:text-${color}-${options.darkText}`);
    }

    if (options.darkBg) {
      classes.push(`dark:bg-${color}-${options.darkBg}`);
    }

    if (options.active) {
      classes.push(`active:bg-${color}-${options.active}`);
    }

    if (options.hover) {
      classes.push(`hover:bg-${color}-${options.hover}`);
    }

    if (options.focus) {
      classes.push(`focus:bg-${color}-${options.focus}`);
    }

    if (options.border) {
      classes.push(`border-${color}-${options.border}`);
    }

    if (options.text) {
      classes.push(`text-${color}-${options.text}`);
    }

    map[color] = classes.join(' ');
  });

  return map;
}

export function useLoading() {
  return {
    doLoading: (callback: () => Promise<any>) => app.get(DialogService).doLoading(callback),
    showLoading: () => app.get(DialogService).showLoading(),
    hideLoading: () => app.get(DialogService).hide(),
  };
}